import React from "react";

function normalizeExternalUrl(raw) {
  if (!raw || typeof raw !== "string") return "";
  const t = raw.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/** @param {{ contactInfo: Record<string, unknown> }} props */
export default function FooterSocialLinks({ contactInfo }) {
  const ci = contactInfo || {};
  const instagramExplicit = normalizeExternalUrl(typeof ci.socialInstagramUrl === "string" ? ci.socialInstagramUrl : "");
  const instagramFallback =
    typeof ci.instagram === "string" && ci.instagram.trim()
      ? `https://instagram.com/${ci.instagram.trim().replace(/^@/, "")}`
      : "";
  const instagram = instagramExplicit || instagramFallback;

  const facebook = normalizeExternalUrl(typeof ci.socialFacebookUrl === "string" ? ci.socialFacebookUrl : "");
  const tiktok = normalizeExternalUrl(typeof ci.socialTiktokUrl === "string" ? ci.socialTiktokUrl : "");
  const linkedin = normalizeExternalUrl(typeof ci.socialLinkedinUrl === "string" ? ci.socialLinkedinUrl : "");

  const items = [
    {
      key: "instagram",
      href: instagram,
      label: "Instagram",
      Icon: IconInstagram,
    },
    {
      key: "facebook",
      href: facebook,
      label: "Facebook",
      Icon: IconFacebook,
    },
    {
      key: "tiktok",
      href: tiktok,
      label: "TikTok",
      Icon: IconTiktok,
    },
    {
      key: "linkedin",
      href: linkedin,
      label: "LinkedIn",
      Icon: IconLinkedin,
    },
  ].filter((x) => x.href);

  if (items.length === 0) return null;

  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {items.map(({ key, href, label, Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#fbd9e5]/25 text-[#fbd9e5]/90 transition-colors hover:border-[#fbd9e5]/50 hover:bg-[#fbd9e5]/10 hover:text-white"
        >
          <Icon className="h-[18px] w-[18px]" aria-hidden />
        </a>
      ))}
    </div>
  );
}

function IconInstagram({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 11-2.881 0 1.44 1.44 0 012.881 0z" />
    </svg>
  );
}

function IconFacebook({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function IconTiktok({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
    </svg>
  );
}

function IconLinkedin({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}
